import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {colors, fonts} from "../../theme/tokens";
import type {Promotion, PromotionCreateRequest, PromotionUpdateRequest} from "../../types/promotion";

function toDateOnly(iso: string): string {
    try {
        return iso.slice(0, 10);
    } catch {
        return "";
    }
}

function fromDateOnly(dateStr: string, endOfDay: boolean): string {
    if (!dateStr) return "";
    return endOfDay ? `${dateStr}T23:59:59.000Z` : `${dateStr}T00:00:00.000Z`;
}

type PromotionFormDialogProps = {
    open: boolean;
    promotion: Promotion | null;
    onClose: () => void;
    onSubmit: (request: PromotionCreateRequest | PromotionUpdateRequest) => void;
    onDelete?: (promotion: Promotion) => void;
    saving: boolean;
    deleting: boolean;
};

const emptyForm = {
    name: "",
    code: "",
    description: "",
    publicName: "",
    publicDescription: "",
    validFrom: "",
    validUntil: "",
    discountDurationMonths: 12,
    discountPercentage: null as number | null,
};

export function PromotionFormDialog({
                                        open,
                                        promotion,
                                        onClose,
                                        onSubmit,
                                        onDelete,
                                        saving,
                                        deleting,
                                    }: PromotionFormDialogProps) {
    const {t} = useTranslation();
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [confirmDelete, setConfirmDelete] = useState(false);

    const isEdit = promotion != null;

    useEffect(() => {
        if (promotion) {
            setForm({
                name: promotion.name,
                code: promotion.code,
                description: promotion.description,
                publicName: promotion.publicName,
                publicDescription: promotion.publicDescription,
                validFrom: toDateOnly(promotion.validFrom),
                validUntil: toDateOnly(promotion.validUntil),
                discountDurationMonths: promotion.discountDuration?.months ?? 12,
                discountPercentage: promotion.discountPercentage ?? null,
            });
        } else {
            setForm(emptyForm);
        }
        setErrors({});
        setConfirmDelete(false);
    }, [promotion, open]);

    const validate = (): boolean => {
        const e: Record<string, string> = {};
        if (!form.name.trim()) e.name = t("PROMOTIONS_VALIDATION_REQUIRED");
        else if (form.name.length > 256) e.name = t("PROMOTIONS_VALIDATION_MAX_256");
        if (!form.code.trim()) e.code = t("PROMOTIONS_VALIDATION_REQUIRED");
        else if (form.code.length > 256) e.code = t("PROMOTIONS_VALIDATION_MAX_256");
        if (form.description.length > 256) e.description = t("PROMOTIONS_VALIDATION_MAX_256");
        if (!form.validFrom) e.validFrom = t("PROMOTIONS_VALIDATION_REQUIRED");
        if (!form.validUntil) e.validUntil = t("PROMOTIONS_VALIDATION_REQUIRED");
        if (form.discountDurationMonths < 1) e.discountDurationMonths = t("PROMOTIONS_VALIDATION_MIN_1");
        if (form.discountPercentage != null && (form.discountPercentage < 0 || form.discountPercentage > 100)) {
            e.discountPercentage = t("PROMOTIONS_VALIDATION_DISCOUNT_RANGE");
        }
        if (form.discountPercentage != null) {
            const decimals = String(form.discountPercentage).split(".")[1];
            if (decimals && decimals.length > 2) e.discountPercentage = t("PROMOTIONS_VALIDATION_MAX_2_DECIMALS");
        }
        if (form.publicName.length > 256) e.publicName = t("PROMOTIONS_VALIDATION_MAX_256");
        if (form.publicDescription.length > 10000) e.publicDescription = t("PROMOTIONS_VALIDATION_MAX_10000");
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = () => {
        if (!validate() || saving) return;
        const validFrom = fromDateOnly(form.validFrom, false);
        const validUntil = fromDateOnly(form.validUntil, true);
        if (isEdit && promotion) {
            onSubmit({
                id: promotion.id,
                name: form.name.trim(),
                code: form.code.trim(),
                description: form.description.trim(),
                publicName: form.publicName.trim(),
                publicDescription: form.publicDescription.trim(),
                validFrom,
                validUntil,
                discountDuration: {months: form.discountDurationMonths},
                discountPercentage: form.discountPercentage,
            });
        } else {
            onSubmit({
                name: form.name.trim(),
                code: form.code.trim(),
                description: form.description.trim(),
                publicName: form.publicName.trim(),
                publicDescription: form.publicDescription.trim(),
                validFrom,
                validUntil,
                discountDuration: {months: form.discountDurationMonths},
                discountPercentage: form.discountPercentage,
            });
        }
    };

    const handleDeleteClick = () => setConfirmDelete(true);
    const handleDeleteConfirm = () => {
        if (promotion && onDelete) {
            onDelete(promotion);
            setConfirmDelete(false);
            onClose();
        }
    };

    const field = (key: keyof typeof form) => ({
        error: Boolean(errors[key]),
        helperText: errors[key],
        size: "small" as const,
        fullWidth: true,
    });

    return (
            <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth slotProps={{paper: {sx: {maxWidth: 720}}}}>
                <DialogTitle>{isEdit ? t("PROMOTIONS_EDIT_TITLE") : t("PROMOTIONS_CREATE_TITLE")}</DialogTitle>
                <DialogContent>
                    <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "repeat(6, 1fr)"}, gap: 2, pt: 1}}>
                        <TextField
                                label={t("PROMOTIONS_LABEL_INTERNAL_NAME")}
                                value={form.name}
                                onChange={(e) => setForm((f) => ({...f, name: e.target.value}))}
                                {...field("name")}
                                required
                                slotProps={{htmlInput: {maxLength: 256}}}
                                sx={{gridColumn: {sm: "span 4"}}}
                        />
                        <TextField
                                label={t("PROMOTIONS_LABEL_CODE")}
                                value={form.code}
                                onChange={(e) => setForm((f) => ({...f, code: e.target.value}))}
                                {...field("code")}
                                required
                                slotProps={{htmlInput: {maxLength: 256, style: {fontFamily: fonts.mono}}}}
                                sx={{gridColumn: {sm: "span 2"}}}
                        />
                        <TextField
                                label={t("PROMOTIONS_LABEL_INTERNAL_DESCRIPTION")}
                                value={form.description}
                                onChange={(e) => setForm((f) => ({...f, description: e.target.value}))}
                                {...field("description")}
                                multiline
                                minRows={2}
                                slotProps={{htmlInput: {maxLength: 256}}}
                                sx={{gridColumn: "1 / -1"}}
                        />
                        <TextField
                                label={t("PROMOTIONS_LABEL_VALID_FROM")}
                                type="date"
                                value={form.validFrom}
                                onChange={(e) => setForm((f) => ({...f, validFrom: e.target.value}))}
                                {...field("validFrom")}
                                required
                                sx={{gridColumn: {sm: "span 2"}}}
                        />
                        <TextField
                                label={t("PROMOTIONS_LABEL_VALID_UNTIL")}
                                type="date"
                                value={form.validUntil}
                                onChange={(e) => setForm((f) => ({...f, validUntil: e.target.value}))}
                                {...field("validUntil")}
                                required
                                sx={{gridColumn: {sm: "span 2"}}}
                        />
                        <TextField
                                label={t("PROMOTIONS_LABEL_DISCOUNT_DURATION")}
                                type="number"
                                value={form.discountDurationMonths}
                                onChange={(e) => setForm((f) => ({...f, discountDurationMonths: parseInt(e.target.value, 10) || 0}))}
                                {...field("discountDurationMonths")}
                                required
                                slotProps={{htmlInput: {min: 1}}}
                                sx={{gridColumn: {sm: "span 2"}}}
                        />
                        <TextField
                                label={t("PROMOTIONS_LABEL_DISCOUNT_PERCENT")}
                                type="number"
                                value={form.discountPercentage ?? ""}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setForm((f) => ({...f, discountPercentage: v === "" ? null : parseFloat(v) || 0}));
                                }}
                                {...field("discountPercentage")}
                                slotProps={{htmlInput: {min: 0, max: 100, step: 0.01}}}
                                sx={{gridColumn: {sm: "span 2"}}}
                        />
                        <TextField
                                label={t("PROMOTIONS_LABEL_PUBLIC_NAME")}
                                value={form.publicName}
                                onChange={(e) => setForm((f) => ({...f, publicName: e.target.value}))}
                                {...field("publicName")}
                                slotProps={{htmlInput: {maxLength: 256}}}
                                sx={{gridColumn: {sm: "span 4"}}}
                        />
                        <TextField
                                label={t("PROMOTIONS_LABEL_PUBLIC_DESCRIPTION")}
                                value={form.publicDescription}
                                onChange={(e) => setForm((f) => ({...f, publicDescription: e.target.value}))}
                                {...field("publicDescription")}
                                multiline
                                minRows={3}
                                slotProps={{htmlInput: {maxLength: 10000}}}
                                sx={{gridColumn: "1 / -1"}}
                        />
                    </Box>
                </DialogContent>
                {confirmDelete ? (
                        <DialogActions sx={{backgroundColor: colors.errorPanelBackground}}>
                            <Typography role="alert" sx={{flex: 1, color: colors.errorPanelText, fontSize: "13px"}}>
                                {t("PROMOTIONS_DELETE_INLINE_CONFIRM")}
                            </Typography>
                            <Button variant="outlined" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                                {t("PROMOTIONS_KEEP")}
                            </Button>
                            <Button color="error" variant="contained" onClick={handleDeleteConfirm} disabled={deleting}>
                                {deleting ? t("INVOICES_DELETING") : t("PROMOTIONS_CONFIRM_DELETE")}
                            </Button>
                        </DialogActions>
                ) : (
                        <DialogActions>
                            {isEdit && onDelete && (
                                    <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDeleteClick} disabled={saving || deleting}>
                                        {t("DELETE")}
                                    </Button>
                            )}
                            <Box sx={{flex: 1}} />
                            <Button variant="outlined" onClick={onClose} disabled={saving || deleting}>
                                {t("CANCEL")}
                            </Button>
                            <Button variant="contained" onClick={handleSubmit} disabled={saving || deleting}>
                                {saving ? t("DETAIL_SAVING") : t("DETAIL_SAVE")}
                            </Button>
                        </DialogActions>
                )}
            </Dialog>
    );
}
